import { Sparkles } from "lucide-react";
import type { AddablePersona } from "@/entities/persona";

interface PersonaInviteProps {
  addable: AddablePersona[];
  /** Vertical of the job's current persona — suggestions prefer the same one. */
  currentVertical: string;
  onPick: (roleSlug: string) => void;
  onOpenAll: () => void;
  /** True while a set-persona mutation is in flight. The card only disappears once
   *  available-variants refetches, so without this a second click would overwrite the
   *  first pick — the Celery task re-reads job.persona instead of taking it as an
   *  argument, so the later request wins and the first generation is silently lost. */
  disabled?: boolean;
}

/** Shown while a project still has a single persona: the same recording, read by
 *  another role. Three suggestions from the current vertical first, then any other. */
export function PersonaInvite({
  addable,
  currentVertical,
  onPick,
  onOpenAll,
  disabled,
}: PersonaInviteProps) {
  const candidates = addable.filter((role) => !role.generated);
  const sameVertical = candidates.filter((role) => role.vertical === currentVertical);
  const suggestions = [
    ...sameVertical,
    ...candidates.filter((role) => role.vertical !== currentVertical),
  ].slice(0, 3);

  if (suggestions.length === 0) return null;

  return (
    <div className="mt-6 rounded-lg border border-dashed p-4 flex flex-col gap-3 bg-muted/30">
      <div className="flex items-start gap-2.5">
        <Sparkles className="w-4 h-4 mt-0.5 text-primary shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium">The same recording, read by another role</p>
          <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
            The transcript is already done. A new role adds its own set of reports on top of it.
          </p>
        </div>
      </div>

      <div className="grid gap-2 sm:grid-cols-3">
        {suggestions.map((role) => (
          <button
            key={role.role_slug}
            type="button"
            onClick={() => onPick(role.role_slug)}
            disabled={disabled}
            className="rounded-lg border bg-background p-2.5 text-left transition-colors hover:border-primary hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:bg-background"
          >
            <span className="block text-[13px] font-medium">{role.role_name}</span>
            <span className="block text-[11px] text-muted-foreground mt-0.5">{role.vertical}</span>
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={onOpenAll}
        className="self-start text-xs text-primary underline underline-offset-2 hover:no-underline"
      >
        See all roles →
      </button>
    </div>
  );
}
